import React from 'react';
import { AudioLines, Moon, MoreHorizontal, ScrollText, Sun } from 'lucide-react';
import Logo from '@/components/ui/logo';
import { CreditsMeter } from '@/components/layout/CreditsMeter';
import { AvatarMenu } from '@/components/layout/AvatarMenu';
import type { Slide } from '@/stores/videoWorkspaceStore';
import { ProjectApps } from './ProjectApps';
import { VideoCreditsUsed } from './VideoCreditsUsed';
import { VideoPublishButton } from './VideoPublishButton';
import { ReviewsToggleButton } from './reviews/ReviewsToggleButton';
import { BarIconB } from './BarIconB';
import { IconTipB } from '@/components/studio/IconTipB';
import { IvyMark } from '@/components/studio/IvyMark';
import { useIvyMarkState } from '@/lib/ivyChat/ivyMarkState';
import { LookChip, type LookFields } from './LookChip';

interface StudioTopBarBProps {
  conversationId: string;
  title?: string;
  hasOutline: boolean;
  onHome: () => void;
  showIvyButton: boolean;
  onOpenIvy: () => void;
  showPublish: boolean;
  slides: Slide[];
  defaultShareTab: 'blog' | 'video';
  audioAvailable: boolean;
  audioOpen: boolean;
  audioIsScripts: boolean;
  onToggleAudio: () => void;
  reviewsOpen: boolean;
  onToggleReviews: () => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  /** The view switch, in the middle of the bar. */
  view?: React.ReactNode;
  /** The open view's own controls after the title (the studio's version switch). */
  titleExtra?: React.ReactNode;
  /** The look the video is made in, named after the title. Not on a phone. */
  look?: LookFields;
  /** Where a view that draws its own controls (the storyboard) puts them, after the title. Not on a phone: More holds them. */
  slotRef?: (el: HTMLDivElement | null) => void;
  /** Plan → Deliver: its chip sits on the right, its line along the bottom of the bar. */
  progress?: React.ReactNode;
  /** On a phone (D9): back, the title or the view switch, and the progress; the rest waits for the phone's menu. */
  phone?: boolean;
  /** On a phone: More opens the sheet with Publish, Audio, Reviews, day/night, Apps and the account. */
  onOpenMore?: () => void;
}

/**
 * Direction B's one top bar (beta only). Left: the logo, Ivy when it is folded, the title and the open view's own
 * controls. Middle: the view switch. Right: Plan → Deliver's chip, the panel icons, Publish, day/night as on home, and
 * the account, which holds the balance. Only an out-of-credits alert stays in the bar, and the progress runs along its foot.
 */
/** Ivy folded away: her mark keeps showing what she is doing. Its own component, so only it re-renders as she streams. */
function FoldedIvyMark() {
  const { state, pulse } = useIvyMarkState();
  return <IvyMark size={16} state={state} pulse={pulse} />;
}

export const StudioTopBarB: React.FC<StudioTopBarBProps> = ({
  conversationId,
  title,
  hasOutline,
  onHome,
  showIvyButton,
  onOpenIvy,
  showPublish,
  slides,
  defaultShareTab,
  audioAvailable,
  audioOpen,
  audioIsScripts,
  onToggleAudio,
  reviewsOpen,
  onToggleReviews,
  isDarkMode,
  onToggleTheme,
  view,
  titleExtra,
  look,
  slotRef,
  progress,
  phone,
  onOpenMore,
}) => (
  <div
    data-studio-topbar="b"
    className="relative z-50 grid h-[var(--b-topbar)] flex-shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 bg-b-chrome pl-3.5 pr-3 shadow-[inset_0_-1px_0_hsl(var(--b-line))]"
  >
    <div className="flex min-w-0 items-center gap-3">
      <IconTipB label="Back to projects">
        <button type="button" onClick={onHome} aria-label="Back to projects" className="flex flex-shrink-0 items-center transition-opacity hover:opacity-80">
          <Logo variant="compact" className="h-6" />
        </button>
      </IconTipB>
      {showIvyButton && (
        <button
          type="button"
          onClick={onOpenIvy}
          aria-label="Open Ivy"
          className="flex h-7 flex-shrink-0 items-center gap-1.5 rounded-[var(--b-radius-control)] bg-b-raised px-2.5 text-b-control text-b-t1 transition-colors hover:bg-b-elevated"
        >
          <FoldedIvyMark />
          Ivy
        </button>
      )}
      {title && !(phone && view) && <h1 className="min-w-0 truncate font-sans text-b-label tracking-normal text-b-t1" title={title}>{title}</h1>}
      {look && !phone && <LookChip outline={look} />}
      {titleExtra && !phone && <div data-bar-title-extra className="flex flex-shrink-0 items-center gap-1.5">{titleExtra}</div>}
      {!phone && <div data-bar-slot ref={slotRef} className="flex flex-shrink-0 items-center empty:hidden" />}
    </div>

    <div data-bar-view className="flex justify-center">{view}</div>

    <div className="flex min-w-0 items-center justify-end gap-1">
      {phone && hasOutline && <VideoCreditsUsed conversationId={conversationId} variant="b" />}
      {phone && <CreditsMeter variant="alert" />}
      {progress}
      {phone && onOpenMore && (
        <BarIconB label="More" onClick={onOpenMore}>
          <MoreHorizontal className="h-[18px] w-[18px]" />
        </BarIconB>
      )}
      {!phone && (<>
        {audioAvailable && (
          <BarIconB label={audioIsScripts ? 'Scripts' : 'Audio & script'} pressed={audioOpen} onClick={onToggleAudio}>
            {audioIsScripts ? <ScrollText className="h-[18px] w-[18px]" /> : <AudioLines className="h-[18px] w-[18px]" />}
          </BarIconB>
        )}
        {hasOutline && <ReviewsToggleButton conversationId={conversationId} variant="icon" active={reviewsOpen} onClick={onToggleReviews} />}
        <ProjectApps projectId={conversationId} trigger="icon" />
        <span aria-hidden="true" className="mx-2 h-5 w-px bg-b-line-strong" />
        {hasOutline && <VideoCreditsUsed conversationId={conversationId} variant="b" />}
        <CreditsMeter variant="alert" />
        {showPublish && (
          <VideoPublishButton
            conversationId={conversationId}
            slideCount={slides.length}
            allSlidesReady={slides.every((s) => s.status === 'completed')}
            slides={slides.map((s) => ({ id: s.id, title: s.title }))}
            defaultShareTab={defaultShareTab}
            compact
          />
        )}
        <BarIconB label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'} onClick={onToggleTheme}>
          {isDarkMode ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </BarIconB>
        <AvatarMenu
          compact
          extra={<CreditsMeter variant="pill" />}
        />
      </>)}
    </div>
  </div>
);
